import React, { useMemo, useState, useEffect, useCallback } from "react";
import config from "../data/siteConfig";
import galleryItems from "../data/gallery";
import { Ornament } from "./Decor";
import { FiX, FiChevronLeft, FiChevronRight, FiZoomIn } from "react-icons/fi";

const STEP = 16;

// Bento rhythm: occasional large / tall / wide tiles, dense-packed so no gaps.
const spanClass = (i) => {
  const m = i % 12;
  if (m === 0) return "row-span-2 sm:col-span-2 sm:row-span-2"; // big feature
  if (m === 4 || m === 9) return "row-span-2"; // tall (suits portrait pieces)
  if (m === 6) return "sm:col-span-2"; // wide (suits landscape shots)
  return "";
};

export default function Gallery({ activeCat, setActiveCat }) {
  const [limit, setLimit] = useState(STEP);
  const [lightbox, setLightbox] = useState(null);

  const filtered = useMemo(
    () => (activeCat === "all" ? galleryItems : galleryItems.filter((g) => g.cat === activeCat)),
    [activeCat]
  );

  useEffect(() => setLimit(STEP), [activeCat]);

  const shown = filtered.slice(0, limit);
  const close = () => setLightbox(null);
  const move = useCallback(
    (dir) => setLightbox((i) => (i === null ? i : (i + dir + filtered.length) % filtered.length)),
    [filtered.length]
  );

  useEffect(() => {
    if (lightbox === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") setLightbox(null);
      if (e.key === "ArrowRight") move(1);
      if (e.key === "ArrowLeft") move(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox, move]);

  const chips = [{ id: "all", name: "All Designs" }, ...config.categories];

  return (
    <section id="gallery" className="relative py-20 md:py-28">
      <div className="container-x">
        <div className="text-center">
          <span className="eyebrow">Our Showcase</span>
          <h2 className="mt-2 font-display text-3xl text-coffee-800 sm:text-4xl">Jewellery Gallery</h2>
          <div className="mt-3 flex justify-center"><Ornament /></div>
          <p className="mx-auto mt-4 max-w-xl text-coffee-700/80">
            Browse our latest designs — filter by category to find your perfect piece.
          </p>
        </div>

        <div className="mt-10 flex flex-wrap justify-center gap-2.5">
          {chips.map((c) => (
            <button
              key={c.id}
              onClick={() => setActiveCat(c.id)}
              className={`chip ${activeCat === c.id ? "chip-active" : ""}`}
            >
              {c.name}
            </button>
          ))}
        </div>

        <div className="mt-10 grid auto-rows-[150px] grid-cols-2 gap-3 sm:auto-rows-[185px] sm:grid-cols-3 lg:grid-cols-4 [grid-auto-flow:dense]">
          {shown.map((g, i) => (
            <button
              key={g.id}
              onClick={() => setLightbox(i)}
              className={`group relative block overflow-hidden rounded-md p-1 ring-1 ring-gold/30 shadow-soft transition-all duration-300 hover:ring-gold hover:shadow-vintage ${spanClass(i)}`}
            >
              <img
                src={g.src}
                alt={g.alt}
                loading="lazy"
                className="h-full w-full rounded-sm object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <span className="pointer-events-none absolute inset-1 flex items-center justify-center rounded-sm bg-gradient-to-t from-coffee-950/70 to-coffee-950/20 text-2xl text-gold-light opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                <FiZoomIn />
              </span>
            </button>
          ))}
        </div>

        {limit < filtered.length && (
          <div className="mt-12 text-center">
            <button className="btn btn-gold" onClick={() => setLimit((l) => l + STEP)}>
              Load More Designs
            </button>
          </div>
        )}
      </div>

      {lightbox !== null && filtered[lightbox] && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-coffee-950/95 p-6" onClick={close}>
          <button className="absolute right-6 top-6 flex h-12 w-12 items-center justify-center rounded-full border border-gold text-xl text-gold-light" onClick={close}><FiX /></button>
          <button className="absolute left-6 top-1/2 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full border border-gold text-2xl text-gold-light"
            onClick={(e) => { e.stopPropagation(); move(-1); }}><FiChevronLeft /></button>
          <img
            src={filtered[lightbox].src}
            alt={filtered[lightbox].alt}
            onClick={(e) => e.stopPropagation()}
            className="max-h-[86vh] max-w-[90vw] rounded-md border-4 border-gold object-contain shadow-vintage"
          />
          <button className="absolute right-6 top-1/2 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full border border-gold text-2xl text-gold-light"
            onClick={(e) => { e.stopPropagation(); move(1); }}><FiChevronRight /></button>
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 rounded-full border border-gold/50 bg-coffee-900/80 px-4 py-1.5 text-xs uppercase tracking-widest text-gold-light">
            {lightbox + 1} / {filtered.length}
          </div>
        </div>
      )}
    </section>
  );
}
